<div class="vendor-page">
  <div class="vendor-shell p-3 p-lg-4">
    <div class="d-flex flex-column flex-xl-row justify-content-between gap-3 align-items-xl-center">
      <div class="d-flex align-items-center gap-3">
        <span class="title-badge">
          <lucide-icon [img]="FileTextIcon" [size]="20"></lucide-icon>
        </span>
        <div>
          <h1 class="page-title-lite">Vendor Management</h1>
          <p class="page-subtitle-lite mb-0">Manage your vendors and clients</p>
        </div>
      </div>

      <div class="d-flex gap-2 flex-wrap justify-content-xl-end align-items-center">
        <button class="btn btn-sm create-btn export export-excel-btn" (click)="exportVendors()" aria-label="Export Excel" title="Export Excel">
          <lucide-icon [img]="FileDownIcon" [size]="16" class="excel-icon"></lucide-icon>
        </button>
        <button class="btn btn-sm create-btn primary" (click)="router.navigateByUrl('/vendors/new')">
          <lucide-icon [img]="PlusIcon" [size]="14"></lucide-icon><span class="ms-2">ADD VENDOR</span>
        </button>
      </div>
    </div>

    <div class="row g-3 mt-1">
      @for (tile of summaryTiles(); track tile.label) {
        <div class="col-12 col-md-6 col-xl">
          <div class="summary-card h-100" role="button" tabindex="0" (click)="applySummaryFilter(tile)" (keydown.enter)="applySummaryFilter(tile)" (keydown.space)="applySummaryFilter(tile)">
            <div class="summary-card-header">
              <div>
                <div class="summary-label d-flex align-items-center gap-2">
                  <lucide-icon [img]="tile.icon" [size]="16"></lucide-icon>
                  <span>{{ tile.label }}</span>
                </div>
              </div>
              <div class="summary-value">{{ tile.count }}</div>
            </div>

            <div class="summary-track">
              @if (tile.count === 0) {
                <div class="summary-segment empty" style="width: 100%"></div>
              } @else {
                @if (tile.rejected > 0) {
                  <div class="summary-segment rejected" [style.width.%]="segmentWidth(tile.rejected, tile.count)">
                    {{ tile.rejected }}
                  </div>
                }
                @if (tile.approved > 0) {
                  <div class="summary-segment approved" [style.width.%]="segmentWidth(tile.approved, tile.count)">
                    {{ tile.approved }}
                  </div>
                }
                @if (tile.pending > 0) {
                  <div class="summary-segment pending" [style.width.%]="segmentWidth(tile.pending, tile.count)">
                    {{ tile.pending }}
                  </div>
                }
              }
            </div>
          </div>
        </div>
      }
    </div>
  </div>

  <div class="panel-card p-3 p-lg-4">
    <div class="toolbar-row mb-3">
      <div class="search-field position-relative flex-grow-1">
        <lucide-icon [img]="SearchIcon" [size]="16" class="search-icon"></lucide-icon>
        <input class="form-control search-input" placeholder="Search client, email, or NOAR ID" [value]="search()" (input)="onSearch(($any($event.target).value || '').toString())" />
      </div>

      <div class="toolbar-filters">
        <div class="filter-field">
          <label class="form-label mb-1">Vendor Type</label>
          <select class="form-select form-select-sm" [value]="vendorType()" (change)="onTypeChange(($any($event.target).value || '').toString())">
            <option value="all">All Types</option>
            @for (type of vendorTypes(); track type) {
              <option [value]="type">{{ type }}</option>
            }
          </select>
        </div>
      </div>
    </div>

    <div class="table-wrap">
      <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
        <table class="table mb-0 align-middle">
          <thead class="table-head sticky-top">
            <tr>
              <th>S.No.</th>
              <th>Client Name</th>
              <th>Type</th>
              <th class="d-none d-lg-table-cell">Generator Type</th>
              <th class="d-none d-md-table-cell">State</th>
              <th class="d-none d-md-table-cell">Region</th>
              <th class="d-none d-lg-table-cell">NOAR ID</th>
              <th class="d-none d-lg-table-cell">CTU</th>
              <th class="d-none d-lg-table-cell">Exchange</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr>
                <td colspan="11" class="text-center py-5 text-muted">Loading Vendor Management...</td>
              </tr>
            } @else if (pagedVendors().length === 0) {
              <tr>
                <td colspan="11" class="text-center py-5 text-muted">Whoops! No Data Found</td>
              </tr>
            }

            @for (vendor of pagedVendors(); track vendor.id; let i = $index) {
              <tr>
                <td>{{ (page() - 1) * pageSize() + i + 1 }}</td>
                <td>
                  <button class="btn btn-link p-0 client-name-link" type="button" (click)="openVendorDetails(vendor)">
                    {{ vendor.vendor_name }}
                  </button>
                </td>
                <td>
                  <span class="metric-pill vendor-type">{{ vendor.vendor_type }}</span>
                </td>
                <td class="d-none d-lg-table-cell">{{ vendor.generator_type || '—' }}</td>
                <td class="d-none d-md-table-cell">{{ vendor.state || '—' }}</td>
                <td class="d-none d-md-table-cell">{{ vendor.region || '—' }}</td>
                <td class="d-none d-lg-table-cell">{{ vendor.noar_id || '—' }}</td>
                <td class="d-none d-lg-table-cell">
                  <span class="metric-pill flag" [class.success]="vendor.connected_with_ctu" [class.muted]="!vendor.connected_with_ctu">
                    {{ vendor.connected_with_ctu ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td class="d-none d-lg-table-cell">
                  <span class="metric-pill flag" [class.success]="vendor.exchange_registered" [class.muted]="!vendor.exchange_registered">
                    {{ vendor.exchange_registered ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span class="status-pill text-capitalize" [class.approved]="vendor.status === 'approved'" [class.pending]="vendor.status === 'pending'" [class.rejected]="vendor.status === 'rejected'">
                    {{ vendor.status }}
                  </span>
                </td>
                <td>
                  <div class="d-flex gap-2">
                    <button class="action-btn edit" title="Edit" (click)="router.navigateByUrl('/vendors/' + vendor.id + '/edit')">
                      <lucide-icon [img]="PencilIcon" [size]="14"></lucide-icon>
                    </button>
                    <button
                      class="action-btn delete"
                      [disabled]="vendor.can_delete === false"
                      [title]="deleteTitle(vendor)"
                      (click)="removeVendor(vendor)"
                    >
                      <lucide-icon [img]="Trash2Icon" [size]="14"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <div class="pager-row mt-3">
      <div class="pager-meta">{{ startRow() }}-{{ endRow() }} of {{ filteredVendors().length }} vendors</div>
      <div class="pagination-shell">
        <button class="pager-btn" [disabled]="page() <= 1" (click)="page.set(1)" aria-label="First page">
          <lucide-icon [img]="ChevronsLeftIcon" [size]="14"></lucide-icon>
        </button>
        <button class="pager-btn" [disabled]="page() <= 1" (click)="page.set(page() - 1)" aria-label="Previous page">
          <lucide-icon [img]="ChevronLeftIcon" [size]="14"></lucide-icon>
        </button>
        <span class="page-indicator current">{{ page() }}</span>
        <button class="pager-btn" [disabled]="page() >= totalPages()" (click)="page.set(page() + 1)" aria-label="Next page">
          <lucide-icon [img]="ChevronRightIcon" [size]="14"></lucide-icon>
        </button>
        <button class="pager-btn" [disabled]="page() >= totalPages()" (click)="page.set(totalPages())" aria-label="Last page">
          <lucide-icon [img]="ChevronsRightIcon" [size]="14"></lucide-icon>
        </button>
        <select class="form-select form-select-sm page-size-select pager-size-select" [value]="pageSize()" (change)="onPageSizeChange(($any($event.target).value || '10').toString())" aria-label="Rows per page">
          @for (size of pageSizes; track size) {
            <option [value]="size">{{ size }}/Page</option>
          }
        </select>
      </div>
    </div>
  </div>
</div>

@if (selectedVendor()) {
  <div class="vendor-modal-backdrop" (click)="closeVendorDetails()"></div>
  <div class="vendor-modal">
    <div class="vendor-modal-card">
      <div class="vendor-modal-header d-flex justify-content-between align-items-center gap-3">
        <div>
          <h6 class="mb-1">Client Basic Details</h6>
          <p class="mb-0">Quick snapshot of the selected vendor profile</p>
        </div>
        <button class="btn btn-sm create-btn export" type="button" (click)="closeVendorDetails()">Close</button>
      </div>
      <div class="vendor-modal-body">
        <div class="row g-3 small">
          <div class="col-md-6">
            <div class="detail-card"><strong>Client Name:</strong> {{ selectedVendor()?.vendor_name || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>Type:</strong> {{ selectedVendor()?.vendor_type || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>Generator Type:</strong> {{ selectedVendor()?.generator_type || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>Email:</strong> {{ selectedVendor()?.email || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>Contact:</strong> {{ selectedVendor()?.contact_number || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>State:</strong> {{ selectedVendor()?.state || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>Region:</strong> {{ selectedVendor()?.region || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>NOAR ID:</strong> {{ selectedVendor()?.noar_id || '-' }}</div>
          </div>
          <div class="col-md-6">
            <div class="detail-card"><strong>Status:</strong> {{ selectedVendor()?.status || '-' }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
}
